import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { v4 as uuidv4 } from "uuid";
import { getRealtimeDatabase } from "src/utils/firebase";
import {
  Button,
  useTheme,
  Box,
  Typography,
  Paper
} from "@mui/material";
import { useOutletContext, useParams } from "react-router-dom";

const Demo = () => {
  const [currentUid, setCurrentUid] = useState("");
  const [copied, setCopied] = useState(false);
  const db = getRealtimeDatabase();
  const theme = useTheme();

  const { type } = useParams(); // e.g. "tractor" or "selfie"

  const setHideSettings = useOutletContext();
  useEffect(() => {
    if (setHideSettings) setHideSettings(true);
    return () => {
      if (setHideSettings) setHideSettings(false);
    };
  }, [setHideSettings]);

  const createNewBarcode = () => {
    const id = uuidv4();
    setCurrentUid(id);

    db.ref(`demo_current_${type}`).set({ uid: id });
    db.ref(`demo_users/${type}/${id}`).set({
      uid: id,
      type: type || "default",
      done: false,
      createdAt: new Date().toISOString()
    });
  };

  const resetBarcode = () => {
    createNewBarcode();
  };

  const copyToClipboard = () => {
    if (currentUid) {
      const fullUrl = `${window.location.origin}/demo_info/${currentUid}/${type}`;
      navigator.clipboard.writeText(fullUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  useEffect(() => {
    if (!db) return undefined;

    const currentRef = db.ref(`demo_current_${type}`);
    currentRef.on("value", (snapshot) => {
      const data = snapshot.val();
      if (data && data.uid) {
        setCurrentUid(data.uid);

        db.ref(`demo_users/${type}/${data.uid}/done`).on("value", (snap) => {
          if (snap.val() === true) {
            console.log(`✅ ${type} demo finished, generating next barcode...`);
            createNewBarcode();
          }
        });
      } else {
        createNewBarcode();
      }
    });

    return () => currentRef.off();
  }, [db, type]);

  const qrSize = 280;

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="calc(100vh - 80px)"
      sx={{
        backgroundColor: theme.palette.background.default,
        p: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 3,
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}
      >
        <Typography variant="h5" gutterBottom align="center">
          Demo QR Generator
        </Typography>

        {type && (
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
          >
            {type.toUpperCase()}
          </Typography>
        )}

        {currentUid && (
          <>
            <Box
              sx={{
                p: 3,
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                mb: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <QRCodeCanvas
                value={`${window.location.origin}/demo_info/${currentUid}/${type}`}
                size={qrSize}
                includeMargin
              />
            </Box>

            <Typography
              variant="body2"
              onClick={copyToClipboard}
              sx={{
                cursor: "pointer",
                color: theme.palette.success.main,
                mb: 2,
                "&:hover": { textDecoration: "underline" }
              }}
            >
              Session ID: {currentUid}
            </Typography>
            {copied && (
              <Typography variant="caption" sx={{ color: "green", mb: 1 }}>
                Copied!
              </Typography>
            )}
          </>
        )}

        <Button
          variant="contained"
          onClick={resetBarcode}
          fullWidth
          sx={{
            mt: "auto",
            py: 1.5,
            fontSize: "16px",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark
            }
          }}
        >
          Reset Barcode
        </Button>
      </Paper>
    </Box>
  );
};

export default Demo;
